import React from 'react';
import { FileTextIcon, UserIcon, BuildingIcon, CalendarIcon, ClockIcon, MapPinIcon, DollarSignIcon, TruckIcon, TagIcon } from 'lucide-react';
export function QuotePreview({
  quoteData,
  quoteType,
  quoteNumber
}) {
  // Valores por defecto para evitar errores
  const customerInfo = quoteData.customerInfo || {};
  const shipping = quoteData.shipping || {};
  const currency = shipping.currency || quoteData.currency || '$';
  const pivots = quoteData.pivots || [];
  const parts = quoteData.parts || [];
  const items = quoteData.items || [];
  const issueDate = quoteData.issueDate || quoteData.date || 'Unknown date';
  const expiryDate = quoteData.expiryDate || 'Unknown';
  return <div className="text-gray-800" data-id="element-4626">
      {/* Header */}
      <div className="flex justify-between items-start mb-8 pb-8 border-b border-gray-200" data-id="element-4627">
        <div data-id="element-4628">
          <div className="flex items-center mb-2" data-id="element-4629">
            <div className="p-2 mr-3 bg-blue-600 rounded-lg" data-id="element-4630">
              <FileTextIcon size={20} className="text-white" data-id="element-4631" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800" data-id="element-4632">
              Quote #{quoteNumber}
            </h1>
          </div>
          <div className="text-gray-600" data-id="element-4633">
            {quoteType} Quote • Created by {quoteData.quotedBy || 'Unknown'}
          </div>
          <div className="mt-4 flex flex-wrap gap-4" data-id="element-4634">
            <div className="flex items-center text-gray-600" data-id="element-4635">
              <CalendarIcon size={16} className="mr-1" data-id="element-4636" />
              Issue Date: {issueDate}
            </div>
            <div className="flex items-center text-gray-600" data-id="element-4637">
              <ClockIcon size={16} className="mr-1" data-id="element-4638" />
              Expiry Date: {expiryDate}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end" data-id="element-4639">
          <div className="text-3xl font-bold text-blue-600" data-id="element-4640">
            {currency}
            {(quoteData.amount || 0).toLocaleString(undefined, {
            maximumFractionDigits: 2
          })}
          </div>
          {quoteData.status && <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm" data-id="element-4641">
              <TagIcon size={14} className="mr-1" data-id="element-4642" />
              {quoteData.status.charAt(0).toUpperCase() + quoteData.status.slice(1)}
            </div>}
        </div>
      </div>

      {/* Customer Information */}
      <div className="mb-8" data-id="element-4643">
        <h2 className="text-xl font-bold mb-4" data-id="element-4644">Customer Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 p-4 rounded-lg" data-id="element-4645">
          <div data-id="element-4646">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4647">Bill To</h3>
            <div className="flex items-start" data-id="element-4648">
              {customerInfo.clientType === 'dealer' || quoteData.clientType === 'dealer' ? <BuildingIcon size={18} className="mr-2 text-gray-400 mt-0.5" data-id="element-4649" /> : <UserIcon size={18} className="mr-2 text-gray-400 mt-0.5" data-id="element-4650" />}
              <div data-id="element-4651">
                <div className="font-medium" data-id="element-4652">
                  {customerInfo.billToCompany || customerInfo.dealerName || quoteData.dealerName || 'Not specified'}
                </div>
                <div className="text-gray-600" data-id="element-4653">
                  {customerInfo.billToAddress || 'No address provided'}
                </div>
                <div className="text-gray-600" data-id="element-4654">
                  {customerInfo.billToCountry || quoteData.country || 'Country not specified'}
                </div>
              </div>
            </div>
          </div>
          <div data-id="element-4655">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4656">Ship To</h3>
            <div className="flex items-start" data-id="element-4657">
              <MapPinIcon size={18} className="mr-2 text-gray-400 mt-0.5" data-id="element-4658" />
              <div data-id="element-4659">
                <div className="font-medium" data-id="element-4660">
                  {customerInfo.shipToCompany || customerInfo.dealerName || quoteData.dealerName || 'Same as billing'}
                </div>
                <div className="text-gray-600" data-id="element-4661">
                  {customerInfo.shipToAddress || 'No address provided'}
                </div>
                <div className="text-gray-600" data-id="element-4662">
                  {customerInfo.shipToCountry || customerInfo.billToCountry || quoteData.country || 'Country not specified'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quote Items */}
      <div className="mb-8" data-id="element-4663">
        <h2 className="text-xl font-bold mb-4" data-id="element-4664">Quote Items</h2>
        <div className="overflow-x-auto" data-id="element-4665">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-4666">
            <thead className="bg-gray-50" data-id="element-4667">
              <tr data-id="element-4668">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4669">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4670">
                  Quantity
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4671">
                  Unit Price
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4672">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-4673">
              {/* Render pivot items if available */}
              {pivots.length > 0 && pivots.map((pivot, index) => <tr key={`pivot-${index}`} data-id="element-4674">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-4675">
                      <div className="font-medium text-gray-900" data-id="element-4676">
                        {pivot.name}
                      </div>
                      <div className="text-gray-500" data-id="element-4677">{pivot.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500" data-id="element-4678">
                      1
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500" data-id="element-4679">
                      {currency}
                      {pivot.price.toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-900 font-medium" data-id="element-4680">
                      {currency}
                      {pivot.price.toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                    </td>
                  </tr>)}
              {/* Render parts items if available */}
              {parts.length > 0 && parts.map((part, index) => <tr key={`part-${index}`} data-id="element-4681">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-4682">
                      <div className="font-medium text-gray-900" data-id="element-4683">
                        {part.name || part.partNumber}
                      </div>
                      <div className="text-gray-500" data-id="element-4684">{part.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500" data-id="element-4685">
                      {part.quantity || 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500" data-id="element-4686">
                      {currency}
                      {(part.price || 0).toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-900 font-medium" data-id="element-4687">
                      {currency}
                      {((part.price || 0) * (part.quantity || 1)).toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                    </td>
                  </tr>)}
              {/* Render project items if available */}
              {items.length > 0 && items.map((item, index) => <tr key={`item-${index}`} data-id="element-4688">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-4689">
                      <div className="font-medium text-gray-900" data-id="element-4690">
                        {item.name}
                      </div>
                      <div className="text-gray-500" data-id="element-4691">{item.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500" data-id="element-4692">
                      {item.quantity || 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500" data-id="element-4693">
                      {currency}
                      {(item.price || 0).toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-900 font-medium" data-id="element-4694">
                      {currency}
                      {((item.price || 0) * (item.quantity || 1)).toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                    </td>
                  </tr>)}
              {/* If no items are available, show a placeholder */}
              {pivots.length === 0 && parts.length === 0 && items.length === 0 && <tr data-id="element-4695">
                    <td colSpan={4} className="px-6 py-4 text-center text-gray-500" data-id="element-4696">
                      No items specified in this quote
                    </td>
                  </tr>}
              {/* Total row */}
              <tr className="bg-gray-50" data-id="element-4697">
                <td colSpan={3} className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-700" data-id="element-4698">
                  Total Amount:
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-blue-600 font-bold" data-id="element-4699">
                  {currency}
                  {(quoteData.amount || 0).toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Shipping & Terms */}
      <div className="mb-8" data-id="element-4700">
        <h2 className="text-xl font-bold mb-4" data-id="element-4701">Shipping & Terms</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-gray-50 p-4 rounded-lg" data-id="element-4702">
          <div data-id="element-4703">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4704">
              Payment Terms
            </h3>
            <div className="flex items-center" data-id="element-4705">
              <DollarSignIcon size={18} className="mr-2 text-gray-400" data-id="element-4706" />
              <div className="font-medium" data-id="element-4707">
                {shipping.paymentTerms || 'Not specified'}
              </div>
            </div>
          </div>
          <div data-id="element-4708">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4709">Incoterm</h3>
            <div className="flex items-center" data-id="element-4710">
              <TruckIcon size={18} className="mr-2 text-gray-400" data-id="element-4711" />
              <div className="font-medium" data-id="element-4712">
                {shipping.incoterm || 'Not specified'}
              </div>
            </div>
          </div>
          <div data-id="element-4713">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4714">Currency</h3>
            <div className="flex items-center" data-id="element-4715">
              <DollarSignIcon size={18} className="mr-2 text-gray-400" data-id="element-4716" />
              <div className="font-medium" data-id="element-4717">{currency}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Notes & Terms */}
      <div className="mb-8" data-id="element-4718">
        <h2 className="text-xl font-bold mb-4" data-id="element-4719">Notes & Terms</h2>
        <div className="bg-gray-50 p-4 rounded-lg" data-id="element-4720">
          <p className="text-gray-600" data-id="element-4721">
            {shipping.notes || quoteData.notes || 'This quote is valid until the expiry date mentioned above. Please contact us if you have any questions.'}
          </p>
        </div>
      </div>
    </div>;
}